import { Box, Modal, Typography, Avatar } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { useTranslate } from "src/locales";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { referenceService } from "src/api/references/referenceService";
import { toLocalISO } from "src/utils/date";
import { aiAssistantService } from "src/api/ai-assistant/aiAssistantService";
import { nurseService } from "src/api/nurse/nurseService";

interface ByzMuiSpeakToTextModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  patientName: string;
  clinicName?: string;
  roomInfo?: string;
  gender?: string | number | null;
  mode?: "followup" | "note";
  patientId?: number | string | null;
}

type SpeechRecognitionLike = {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
  maxAlternatives?: number;
  onresult?: (event: any) => void;
  onstart?: () => void;
  onend?: () => void;
  onerror?: () => void;
  start: () => void;
  stop: () => void;
};

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: {
    xs: "calc(100vw - 24px)",
    sm: 520,
    md: 620,
  },
  maxWidth: "calc(100vw - 24px)",
  bgcolor: "#fff",
  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1), 0px 10px 15px rgba(0, 0, 0, 0.1)",
  borderRadius: "16px",
  p: { xs: "20px 0px", sm: "24px 0px" },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "18px",
  fontFamily: "Poppins, sans-serif",
};

export function ByzMuiSpeakToTextModal(props: Readonly<ByzMuiSpeakToTextModalProps>) {
  const {
    open,
    onClose,
    onSaved,
    patientName,
    clinicName,
    roomInfo,
    gender,
    mode = "followup",
  } = props;
  const { t } = useTranslate();

  const addFollowupLabel =
    t("nurse.speak-to-text.add-followup") || "İzlem Ekle";
  const noteLabel = t("nurse.speak-to-text.note-label") || "Giriniz";
  const addNoteLabel = t("nurse.speak-to-text.add-note") || "Not Ekle";

  const normalizedGender = String(gender ?? "")
    .toLowerCase()
    .trim();
  const isFemale = ["female", "kadin", "kadın", "2", "k", "bayan"].includes(
    normalizedGender
  );
  const genderIconSrc = isFemale
    ? "/assets/icons/nurse/woman.svg"
    : "/assets/icons/nurse/man.svg";

  const [noteValue, setNoteValue] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [isFormatting, setIsFormatting] = useState(false);
  const [isPrefilling, setIsPrefilling] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const noteInputRef = useRef<HTMLTextAreaElement | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const shouldKeepRecordingRef = useRef(false);
  const noteValueRef = useRef("");
  const formatInFlightRef = useRef(false);

  useEffect(() => {
    noteValueRef.current = noteValue;
  }, [noteValue]);


  useEffect(() => {
    if (!open) {
      setNoteValue("");
      noteValueRef.current = "";
      return;
    }

    setNoteValue("");
    noteValueRef.current = "";
    setIsFormatting(false);
  }, [open, mode, props.patientId]);
  const formatCurrentDictationIfAny = async (): Promise<string | null> => {
    const raw = (noteValueRef.current || "").trim();
    if (!raw) return null;
    if (formatInFlightRef.current) return null;

    formatInFlightRef.current = true;
    setIsFormatting(true);
    try {
      const res = await aiAssistantService.formatDictation({
        text: raw,
      });
      const ok =
        (res as any)?.success === true ||
        (res as any)?.issuccess === true ||
        (res as any)?.isSuccess === true;
      const formatted = (res as any)?.data;
      if (ok && typeof formatted === "string" && formatted.trim()) {
        setNoteValue(formatted);
        return formatted.trim();
      }
      // Hata/başarısızlık durumunda hiçbir şey yapma: ham metin kalsın.
    } catch {
      // Sessizce yut: ham metin kalsın.
    } finally {
      setIsFormatting(false);
      formatInFlightRef.current = false;
    }
    return null;
  };

  const stopRecognition = () => {
    shouldKeepRecordingRef.current = false;
    setIsRecording(false);
    try {
      recognitionRef.current?.stop?.();
    } catch {
    }
    // Konuşma bittiğinde dikteyi AI ile düzenle (fail olursa ham metin kalsın)
  };

  const startRecognition = () => {
    if (noteInputRef.current) {
      noteInputRef.current.focus();
    }

    const SpeechRecognitionCtor =
      (globalThis as any).SpeechRecognition ||
      (globalThis as any).webkitSpeechRecognition;

    if (!SpeechRecognitionCtor) {
      return;
    }

    try {
      if (!recognitionRef.current) {
        const recognition = new SpeechRecognitionCtor();
        recognition.lang = "tr-TR";
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event: any) => {
          const results = event?.results;
          if (!results) return;

          let finalText = "";
          for (let i = event.resultIndex ?? 0; i < results.length; i += 1) {
            const res = results[i];
            const transcript = res?.[0]?.transcript?.toString?.() ?? "";
            if (res?.isFinal && transcript) {
              finalText += (finalText ? " " : "") + transcript.trim();
            }
          }

          if (!finalText) return;
          setNoteValue((prev) => (prev ? `${prev.trim()}\n${finalText}` : finalText));
        };

        recognition.onstart = () => {
          setIsRecording(true);
        };

        recognition.onend = () => {
          if (shouldKeepRecordingRef.current) {
            try {
              recognition.start();
              return;
            } catch {
            }
          }
          setIsRecording(false);
          // Kullanıcı kayıtı kapattıysa / kayıt bitti ise formatla
        };

        recognition.onerror = () => {
          if (!shouldKeepRecordingRef.current) return;
          shouldKeepRecordingRef.current = false;
          setIsRecording(false);
        };

        recognitionRef.current = recognition as unknown as SpeechRecognitionLike;
      }

      shouldKeepRecordingRef.current = true;
      setIsRecording(true);
      recognitionRef.current.start();
    } catch {
      // Hata durumunda ekstra işlem yapma
    }
  };

  let headerTitle = "";
  if (mode === "note") {
    headerTitle = addNoteLabel === "nurse.speak-to-text.add-note" ? "Not Ekle" : addNoteLabel;
  } else {
    headerTitle = addFollowupLabel === "nurse.speak-to-text.add-followup" ? "İzlem Ekle" : addFollowupLabel;
  }

  const handleMicClick = () => {
    if (isRecording) {
      stopRecognition();
      return;
    }
    startRecognition();
  };

  const handleSave = async () => {
    if (!props.patientId) return;
    try {
      const trimmed = String(noteValueRef.current ?? noteValue ?? "").trim();
      const noteToSave = mode === "note" && !trimmed ? " " : trimmed;
      if (!noteToSave && mode !== "note") return;

      setSaveLoading(true);
      if (mode === "followup") {
        await referenceService.patient.AddPatientMonitoring({
          pt_id: Number(props.patientId),
          note: noteToSave,
          write_on_epicrise: "0",
          ptime: toLocalISO(new Date()),
        });
      } else {
        await nurseService.nurse.upsertPatientNote<{
          success?: boolean;
          issuccess?: boolean;
          message?: string;
          data?: any;
        }>({
          ptid: Number(props.patientId),
          note: noteToSave,
        });
      }
      setSnackbarOpen(true);
      onSaved?.();
      onClose();
      setNoteValue("");
      noteValueRef.current = "";
    } finally {
      setSaveLoading(false);
    }
  };

  const handleAiFormat = async () => {
    if (isRecording || isFormatting || isPrefilling || saveLoading) return;
    await formatCurrentDictationIfAny();
  };

  const extractNote = (payload: any): string => {
    if (!payload) return "";
    if (typeof payload === "string") {
      const s = payload.trim();
      try {
        const parsed = JSON.parse(s);
        return String(parsed?.note ?? "");
      } catch {
      }
      const re = /note\s*:\s*['"]([\s\S]*?)['"]/i;
      const m = re.exec(s);
      return m?.[1] ? String(m[1]) : "";
    }
    if (typeof payload === "object") {
      return String(payload?.note ?? "");
    }
    return "";
  };

  useEffect(() => {
    if (!open) return;
    if (mode !== "note") return;
    if (!props.patientId) return;

    let alive = true;
    setIsPrefilling(true);
    nurseService.nurse.getPatientNote<{
      success?: boolean;
      issuccess?: boolean;
      data?: any;
      message?: string;
    }>({
      ptId: Number(props.patientId),
    })
      .then((res) => {
        if (!alive) return;
        const ok = (res as any)?.success === true || (res as any)?.issuccess === true;
        if (!ok) return;
        const note = extractNote((res as any)?.data);
        setNoteValue(note || "");
        noteValueRef.current = note || "";
      })
      .catch(() => {
      })
      .finally(() => {
        if (!alive) return;
        setIsPrefilling(false);
      });

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, props.patientId]);

  // Ensure recognition stops when modal closes/unmounts
  useEffect(() => {
    if (open) return;
    stopRecognition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => {
      stopRecognition();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
        {/* Header */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2.5,
            px: { xs: 2, sm: 3 },
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, letterSpacing: 0.15, lineHeight: 1.5 }}
          >
            {headerTitle}
          </Typography>

          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        {/* Patient info banner */}
        <Box
          sx={{
            alignSelf: "stretch",
            minHeight: 92,
            backgroundColor: "#f0f7ff",
            borderBottom: "1px solid #e0e9f5",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            px: { xs: 2, sm: 3 },
            fontSize: 14,
            color: "#1f2937",
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: { xs: "center", sm: "flex-start" },
              gap: { xs: 1.5, sm: 2.5 },
            }}
          >
            <Avatar
              sx={{
                width: { xs: 44, sm: 50 },
                height: { xs: 44, sm: 50 },
                bgcolor: "#f4f7fe",
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={genderIconSrc}
                alt={isFemale ? "female" : "male"}
                sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
              />
            </Avatar>

            <Box
              sx={{
                minWidth: 0,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 14, sm: 16 },
                  letterSpacing: "-0.5px",
                  lineHeight: { xs: "18px", sm: "20px" },
                  fontWeight: 600,
                  wordBreak: "break-word",
                }}
              >
                {patientName}
              </Typography>
              {clinicName && (
                <Typography
                  sx={{
                    fontWeight: 600,
                    letterSpacing: "-0.5px",
                    lineHeight: 1.4,
                    wordBreak: "break-word",
                  }}
                >
                  {clinicName}
                </Typography>
              )}
              {roomInfo && (
                <Typography
                  sx={{
                    fontWeight: 600,
                    letterSpacing: "-0.5px",
                    lineHeight: 1.4,
                    wordBreak: "break-word",
                  }}
                >
                  {roomInfo}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Content area: textarea + save button */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1.5,
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ByzMuiSaveButton
              onClick={handleSave}
              loading={saveLoading}
              disabled={isFormatting || isPrefilling}
            />
            <Box
              component="button"
              type="button"
              onClick={handleAiFormat}
              disabled={isRecording || isFormatting || isPrefilling || saveLoading}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                border: "1px solid #2F73FE",
                backgroundColor: "#F4F7FE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor:
                  isRecording || isFormatting || isPrefilling || saveLoading
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  isRecording || isFormatting || isPrefilling || saveLoading
                    ? 0.6
                    : 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor:
                    isRecording || isFormatting || isPrefilling || saveLoading
                      ? "#F4F7FE"
                      : "#E7F0FF",
                },
              }}
            >
              <Box
                component="img"
                src="/assets/icons/ai/ai-nurse-icon.png"
                alt="ai-nurse"
                sx={{ width: 22, height: 22, objectFit: "contain" }}
              />
            </Box>
          </Box>

          <Box sx={{ position: "relative", width: "100%" }}>
            <ByzMuiTextField
              fullWidth
              multiline
              minRows={8}
              label={
                noteLabel === "nurse.speak-to-text.note-label"
                  ? "Giriniz"
                  : noteLabel
              }
              placeholder=""
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              inputRef={noteInputRef}
              disabled={isFormatting || isPrefilling}
              sx={{
                "& .MuiInputBase-root": {
                  paddingBottom: "20px",
                },
              }}
            />
            {isPrefilling && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 2,
                  borderRadius: 2,
                  pointerEvents: "none",
                  overflow: "hidden",
                  "@keyframes shimmerPrefill": {
                    "0%": { transform: "translateX(-100%)" },
                    "100%": { transform: "translateX(100%)" },
                  },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(244, 247, 254, 0.65)",
                    backdropFilter: "blur(1px)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: "45%",
                    background:
                      "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(47,115,254,0.14) 50%, rgba(255,255,255,0) 100%)",
                    animation: "shimmerPrefill 1.05s ease-in-out infinite",
                  }}
                />
              </Box>
            )}
            {isFormatting && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 2,
                  borderRadius: 2,
                  pointerEvents: "none",
                  overflow: "hidden",
                  "@keyframes shimmer": {
                    "0%": { transform: "translateX(-100%)" },
                    "100%": { transform: "translateX(100%)" },
                  },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(244, 247, 254, 0.75)",
                    backdropFilter: "blur(1px)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: "45%",
                    background:
                      "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(47,115,254,0.18) 50%, rgba(255,255,255,0) 100%)",
                    animation: "shimmer 1.1s ease-in-out infinite",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: 10,
                    px: 1,
                    py: 0.5,
                    borderRadius: 999,
                    bgcolor: "rgba(47,115,254,0.10)",
                    color: "#2f73fe",
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                  }}
                >
                  Ai düzenliyor…
                </Box>
              </Box>
            )}
            <Box
              sx={{
                position: "absolute",
                right: 12,
                bottom: 5,
                width: 44,
                height: 30,
                borderRadius: 999,
                bgcolor: isRecording ? "#4E657F" : "#F4F7FE", // Neutral/10
                border: "1px solid",
                borderColor: isRecording ? "#4E657F" : "#1F2937", // Neutral-New/100
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow:
                  "0px 1px 3px rgba(15, 23, 42, 0.25)",
                cursor: isFormatting ? "not-allowed" : "pointer",
                "&:hover": {
                  bgcolor: isRecording ? "#4E657F" : "#E4EBF5",
                },
              }}
              onClick={isFormatting ? undefined : handleMicClick}
            >
              <Box
                component="img"
                src="/assets/icons/generals/microphone.svg"
                alt="microphone"
                sx={{
                  width: 18,
                  height: 18,
                  filter: isRecording ? "invert(1)" : "none",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      </Modal>

      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title={t("save-success")}
        message=""
        type="success"
      />
    </>
  );
}

